import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Upload,
  LogOut,
  Sparkles,
  BookOpen,
  Library,
  HeartHandshake,
  ShoppingBag,
  RefreshCw,
  Trash2,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
} from "lucide-react";
import { api, clearAdminToken } from "@/lib/api";
import AdminWeeklySchedule from "@/pages/AdminWeeklySchedule";
import AnalyticsCard from "@/components/admin/AnalyticsCard";
import AdminDailyContent from "@/components/admin/AdminDailyContent";
import AdminDailySubscribers from "@/components/admin/AdminDailySubscribers";
import AdminSeries from "@/components/admin/AdminSeries";
import AdminPlans from "@/components/admin/AdminPlans";
import AdminDailyTheme from "@/components/admin/AdminDailyTheme";
import AdminDailyMonthly from "@/components/admin/AdminDailyMonthly";
import AdminWeeklyThemes from "@/components/admin/AdminWeeklyThemes";
import AdminSpiritualDirection from "@/components/admin/AdminSpiritualDirection";
import AdminEditorialCouncil from "@/components/admin/AdminEditorialCouncil";
import AdminLeads from "@/components/admin/AdminLeads";
import AdminUsers from "@/components/admin/AdminUsers";
import AdminSystemCheck from "@/components/admin/AdminSystemCheck";
import AdminReports from "@/components/admin/AdminReports";

const CATEGORIES = [
  "Graça",
  "Fé",
  "Família",
  "Cruz",
  "Criação",
  "Arrependimento",
  "Armas Espirituais",
  "Amor",
  "Aliança",
  "Alegria",
  "Esperança",
  "Obediência",
  "Santidade",
  "Adoração",
];

function StatCard({ icon: Icon, label, value, sub, testId }) {
  return (
    <div
      data-testid={testId}
      className="bg-white border border-[#0B1E36]/10 p-6"
    >
      <div className="flex items-center gap-3 text-[#C5A059]">
        <Icon size={16} strokeWidth={1.5} />
        <span className="text-[0.65rem] uppercase tracking-[0.28em] font-medium">
          {label}
        </span>
      </div>
      <div className="mt-4 font-serif-display text-4xl text-[#0B1E36] tabular-nums leading-none">
        {value}
      </div>
      {sub && <div className="mt-2 text-xs text-[#0B1E36]/60">{sub}</div>}
    </div>
  );
}

function JobStatus({ status }) {
  const cfg = {
    queued: { icon: Clock, color: "text-[#0B1E36]/50", label: "Na fila" },
    ocr: { icon: Loader2, color: "text-[#C5A059] animate-spin", label: "OCR em andamento" },
    inserting: { icon: Loader2, color: "text-[#C5A059] animate-spin", label: "Inserindo" },
    done: { icon: CheckCircle2, color: "text-emerald-600", label: "Concluído" },
    failed: { icon: XCircle, color: "text-rose-600", label: "Falhou" },
  }[status] || { icon: Clock, color: "text-[#0B1E36]/50", label: status };
  const I = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-2 text-[0.72rem] uppercase tracking-[0.2em] ${cfg.color}`}>
      <I size={12} strokeWidth={1.5} />
      {cfg.label}
    </span>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  // Upload state
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState("Graça");
  const [source, setSource] = useState("");
  const [customCategory, setCustomCategory] = useState("");
  const [uploadMsg, setUploadMsg] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Batch derivatives
  const [batchCategory, setBatchCategory] = useState("");
  const [batchLimit, setBatchLimit] = useState(25);
  const [batchMsg, setBatchMsg] = useState(null);
  const [batchRunning, setBatchRunning] = useState(false);

  const loadOverview = useCallback(() => {
    api
      .get("/admin/overview")
      .then(({ data }) => setOverview(data))
      .catch((err) => {
        if (err?.response?.status === 401) {
          clearAdminToken();
          navigate("/admin/login", { replace: true });
        }
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => {
    loadOverview();
    const t = setInterval(loadOverview, 5000);
    return () => clearInterval(t);
  }, [loadOverview]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {}
    clearAdminToken();
    navigate("/admin/login", { replace: true });
  };

  const submitUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    setUploadMsg(null);
    const finalCategory = customCategory.trim() || category;
    const finalSource = source.trim() || file.name.replace(/\.pdf$/i, "");
    const form = new FormData();
    form.append("file", file);
    form.append("category", finalCategory);
    form.append("source", finalSource);
    try {
      const { data } = await api.post("/admin/sermons/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadMsg({ ok: true, text: `Upload enviado. Job ${data.job_id.slice(0, 8)}…` });
      setFile(null);
      setSource("");
      setCustomCategory("");
      loadOverview();
    } catch (err) {
      setUploadMsg({
        ok: false,
        text:
          err?.response?.data?.detail ||
          "Falha no upload. Verifique o arquivo e tente novamente.",
      });
    } finally {
      setUploading(false);
    }
  };

  const runBatch = async () => {
    setBatchRunning(true);
    setBatchMsg(null);
    try {
      const params = { limit: batchLimit };
      if (batchCategory) params.category = batchCategory;
      const { data } = await api.post("/admin/derivatives/batch", null, {
        params,
      });
      setBatchMsg({
        ok: true,
        text: `${data.queued} sermões enfileirados. A geração acontece em background (5-10s por sermão).`,
      });
    } catch (err) {
      setBatchMsg({
        ok: false,
        text: err?.response?.data?.detail || "Falha ao enfileirar.",
      });
    } finally {
      setBatchRunning(false);
    }
  };

  if (loading && !overview) {
    return (
      <div className="min-h-screen bg-[#FAF8F4] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#C5A059]" size={32} />
      </div>
    );
  }

  if (!overview) return null;
  const { sermons, books, devotionals, prayers, purchases, jobs } = overview;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAF8F4]">
      <header className="bg-[#0B1E36] text-white">
        <div className="max-w-7xl mx-auto px-6 py-8 flex items-center justify-between">
          <div>
            <p className="text-[0.68rem] uppercase tracking-[0.3em] text-[#C5A059]">
              Painel Administrativo
            </p>
            <h1 className="mt-2 font-serif-display text-3xl md:text-4xl tracking-[-0.02em]">
              Carlos Medeiros
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="text-xs uppercase tracking-[0.22em] text-white/70 hover:text-[#C5A059]"
            >
              Ver site
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 bg-white/[0.06] hover:bg-white/10 border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.22em] transition-colors"
            >
              <LogOut size={13} strokeWidth={1.5} />
              Sair
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Overview cards */}
        <div
          data-testid="admin-overview-cards"
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4"
        >
          <StatCard
            testId="admin-stat-sermons"
            icon={Library}
            label="Sermões"
            value={sermons.total}
            sub={`${sermons.with_derivatives} com derivados`}
          />
          <StatCard
            testId="admin-stat-books"
            icon={BookOpen}
            label="Livros"
            value={books}
          />
          <StatCard
            testId="admin-stat-devotionals"
            icon={BookOpen}
            label="Devocionais"
            value={devotionals}
          />
          <StatCard
            testId="admin-stat-prayers"
            icon={HeartHandshake}
            label="Orações"
            value={prayers}
          />
          <StatCard
            testId="admin-stat-purchases"
            icon={ShoppingBag}
            label="Pedidos"
            value={purchases.total}
            sub={`${purchases.paid} pagos`}
          />
        </div>

        <AnalyticsCard />

        <AdminSystemCheck />

        <AdminReports />

        <AdminDailyTheme />

        <AdminDailyMonthly />

        <AdminWeeklyThemes />

        <AdminSpiritualDirection />

        <AdminEditorialCouncil />

        <AdminDailyContent />

        <AdminSeries />

        <AdminPlans />

        <AdminDailySubscribers />

        <AdminUsers />

        <AdminLeads />

        {/* Upload section */}
        <section className="mt-14 bg-white border border-[#0B1E36]/10 p-8">
          <div className="flex items-center gap-3 text-[#C5A059] mb-6">
            <Upload size={16} strokeWidth={1.5} />
            <h2 className="text-[0.7rem] uppercase tracking-[0.28em] font-medium">
              Novo PDF de sermões
            </h2>
          </div>

          <form
            onSubmit={submitUpload}
            data-testid="admin-upload-form"
            className="grid grid-cols-1 md:grid-cols-12 gap-6"
          >
            <div className="md:col-span-5">
              <label className="text-[0.7rem] uppercase tracking-[0.22em] text-[#0B1E36]/60">
                Arquivo PDF
              </label>
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="admin-upload-file"
                required
                className="mt-2 w-full text-sm file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-[#0B1E36] file:text-white file:text-xs file:uppercase file:tracking-[0.2em] file:cursor-pointer"
              />
            </div>
            <div className="md:col-span-3">
              <label className="text-[0.7rem] uppercase tracking-[0.22em] text-[#0B1E36]/60">
                Categoria
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                data-testid="admin-upload-category"
                className="mt-2 w-full border border-[#0B1E36]/15 py-2 px-3 text-sm bg-white"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={customCategory}
                onChange={(e) => setCustomCategory(e.target.value)}
                data-testid="admin-upload-custom-category"
                placeholder="ou nova categoria…"
                className="mt-2 w-full border border-[#0B1E36]/15 py-2 px-3 text-sm bg-white"
              />
            </div>
            <div className="md:col-span-4">
              <label className="text-[0.7rem] uppercase tracking-[0.22em] text-[#0B1E36]/60">
                Nome da coleção (source)
              </label>
              <input
                type="text"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                data-testid="admin-upload-source"
                placeholder="Ex: 100 Sermões sobre Perseverança"
                className="mt-2 w-full border border-[#0B1E36]/15 py-2 px-3 text-sm bg-white"
              />
            </div>

            <div className="md:col-span-12 flex items-center justify-between gap-6 flex-wrap">
              {uploadMsg && (
                <div
                  data-testid="admin-upload-msg"
                  className={`text-xs px-3 py-2 border ${
                    uploadMsg.ok
                      ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                      : "border-rose-300 bg-rose-50 text-rose-800"
                  }`}
                >
                  {uploadMsg.text}
                </div>
              )}
              <button
                type="submit"
                disabled={!file || uploading}
                data-testid="admin-upload-submit"
                className="ml-auto btn-primary-gold"
              >
                {uploading ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Enviando…
                  </>
                ) : (
                  <>
                    <Upload size={14} strokeWidth={1.5} />
                    Enviar PDF
                  </>
                )}
              </button>
            </div>
          </form>

          <p className="mt-6 text-xs text-[#0B1E36]/55 leading-relaxed">
            O processamento acontece em segundo plano (OCR + separação de
            sermões + inserção no banco). Para PDFs grandes (100 sermões) leva
            de 3 a 5 minutos. Acompanhe o status abaixo.
          </p>
        </section>

        {/* Upload jobs list */}
        <section className="mt-10" data-testid="admin-jobs-section">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-[0.7rem] uppercase tracking-[0.28em] text-[#0B1E36]/70 font-medium">
              Últimos uploads
            </h2>
            <button
              onClick={loadOverview}
              data-testid="admin-refresh-jobs"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#0B1E36]/70 hover:text-[#C5A059]"
            >
              <RefreshCw size={13} strokeWidth={1.5} />
              Atualizar
            </button>
          </div>
          {jobs.length === 0 ? (
            <div className="py-10 text-center text-sm text-[#0B1E36]/50 border border-dashed border-[#0B1E36]/15">
              Nenhum upload feito pelo painel ainda.
            </div>
          ) : (
            <ul className="divide-y divide-[#0B1E36]/8 border-y border-[#0B1E36]/8">
              {jobs.map((j) => (
                <li
                  key={j.id}
                  data-testid={`admin-job-${j.id}`}
                  className="py-4 grid grid-cols-12 gap-4 items-center"
                >
                  <div className="col-span-12 md:col-span-5">
                    <div className="text-sm text-[#0B1E36] font-medium truncate">
                      {j.source}
                    </div>
                    <div className="text-xs text-[#0B1E36]/55 truncate">
                      {j.filename}
                    </div>
                  </div>
                  <div className="col-span-6 md:col-span-2 text-xs text-[#0B1E36]/60">
                    {j.category}
                  </div>
                  <div className="col-span-6 md:col-span-3">
                    <JobStatus status={j.status} />
                  </div>
                  <div className="col-span-12 md:col-span-2 text-xs text-[#0B1E36]/60 tabular-nums text-right">
                    {j.inserted ? `${j.inserted} sermões` : j.detected ? `${j.detected} detectados` : "·"}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Batch derivatives */}
        <section className="mt-14 bg-[#0B1E36] text-white p-8">
          <div className="flex items-center gap-3 text-[#C5A059] mb-6">
            <Sparkles size={16} strokeWidth={1.5} />
            <h2 className="text-[0.7rem] uppercase tracking-[0.28em] font-medium">
              Gerar derivados em lote
            </h2>
          </div>
          <p className="text-white/70 text-sm max-w-2xl leading-relaxed mb-6">
            Para cada sermão selecionado, a IA (Claude Sonnet 4.5) gera 4
            derivados: devocional, mensagem do dia, frase de impacto e frase de
            aplicação prática. Apenas sermões ainda sem derivados serão
            processados.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
            <div className="md:col-span-4">
              <label className="text-[0.7rem] uppercase tracking-[0.22em] text-white/50">
                Categoria (opcional)
              </label>
              <select
                value={batchCategory}
                onChange={(e) => setBatchCategory(e.target.value)}
                data-testid="admin-batch-category"
                className="mt-2 w-full bg-white/5 border border-white/15 py-2 px-3 text-sm text-white"
              >
                <option value="">Todas</option>
                {sermons.by_category.map((c) => (
                  <option key={c.category} value={c.category}>
                    {c.category} ({c.count})
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="text-[0.7rem] uppercase tracking-[0.22em] text-white/50">
                Quantidade
              </label>
              <input
                type="number"
                min={1}
                max={200}
                value={batchLimit}
                onChange={(e) => setBatchLimit(Number(e.target.value))}
                data-testid="admin-batch-limit"
                className="mt-2 w-full bg-white/5 border border-white/15 py-2 px-3 text-sm text-white tabular-nums"
              />
            </div>
            <div className="md:col-span-5 flex justify-end">
              <button
                onClick={runBatch}
                disabled={batchRunning}
                data-testid="admin-batch-run"
                className="inline-flex items-center gap-2 bg-[#C5A059] hover:bg-[#B58F4A] text-[#0B1E36] px-6 py-3 text-xs uppercase tracking-[0.22em] font-medium transition-colors disabled:opacity-60"
              >
                {batchRunning ? (
                  <>
                    <Loader2 size={13} className="animate-spin" />
                    Enfileirando…
                  </>
                ) : (
                  <>
                    <Sparkles size={13} strokeWidth={1.5} />
                    Gerar derivados
                  </>
                )}
              </button>
            </div>
          </div>
          {batchMsg && (
            <div
              data-testid="admin-batch-msg"
              className={`mt-6 text-xs px-3 py-2 border ${
                batchMsg.ok
                  ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-100"
                  : "border-rose-400/40 bg-rose-400/10 text-rose-100"
              }`}
            >
              {batchMsg.text}
            </div>
          )}
        </section>

        {/* Sermon manager */}
        <AdminSermonManager onChange={loadOverview} categories={sermons.by_category} />

        {/* Weekly schedule */}
        <AdminWeeklySchedule />

        <div className="h-20" />
      </main>
    </div>
  );
}

// Separate component for sermon search + delete
function AdminSermonManager({ onChange, categories }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: 15 };
    if (q) params.q = q;
    if (cat) params.category = cat;
    api
      .get("/sermons", { params })
      .then(({ data }) => {
        setItems(data.items || []);
        setTotal(data.total || 0);
      })
      .finally(() => setLoading(false));
  }, [q, cat]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const remove = async (ident) => {
    if (!window.confirm("Excluir este sermão definitivamente?")) return;
    setDeleting(ident);
    try {
      await api.delete(`/admin/sermons/${ident}`);
      load();
      onChange?.();
    } finally {
      setDeleting(null);
    }
  };

  return (
    <section className="mt-14" data-testid="admin-sermon-manager">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h2 className="text-[0.7rem] uppercase tracking-[0.28em] text-[#0B1E36]/70 font-medium">
          Gerenciar sermões ({total} no filtro atual)
        </h2>
        <div className="flex gap-3 flex-wrap">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="admin-sermon-search"
            placeholder="Buscar…"
            className="border border-[#0B1E36]/15 bg-white py-2 px-3 text-sm w-64"
          />
          <select
            value={cat}
            onChange={(e) => setCat(e.target.value)}
            data-testid="admin-sermon-category-filter"
            className="border border-[#0B1E36]/15 bg-white py-2 px-3 text-sm"
          >
            <option value="">Todas categorias</option>
            {categories?.map((c) => (
              <option key={c.category} value={c.category}>
                {c.category} ({c.count})
              </option>
            ))}
          </select>
        </div>
      </div>
      {loading && items.length === 0 ? (
        <div className="py-10 text-center text-sm text-[#0B1E36]/40">Carregando…</div>
      ) : items.length === 0 ? (
        <div className="py-10 text-center text-sm text-[#0B1E36]/50">
          Nenhum sermão para os filtros atuais.
        </div>
      ) : (
        <ul className="divide-y divide-[#0B1E36]/8 border-y border-[#0B1E36]/8">
          {items.map((s) => (
            <li
              key={s.id}
              data-testid={`admin-sermon-row-${s.slug}`}
              className="py-4 grid grid-cols-12 gap-4 items-center"
            >
              <div className="col-span-12 md:col-span-6">
                <Link
                  to={`/pregacoes/${s.slug}`}
                  className="text-sm text-[#0B1E36] font-medium hover:text-[#C5A059]"
                >
                  {s.title}
                </Link>
                <div className="text-xs text-[#0B1E36]/55 truncate">
                  {s.verse_reference || "·"}
                </div>
              </div>
              <div className="col-span-4 md:col-span-2 text-xs text-[#0B1E36]/60">
                {s.category}
              </div>
              <div className="col-span-4 md:col-span-2 text-xs text-[#0B1E36]/60">
                {s.derivatives?.devotional ? (
                  <span className="text-emerald-700">✓ IA</span>
                ) : (
                  <span className="text-[#0B1E36]/30">sem IA</span>
                )}
              </div>
              <div className="col-span-4 md:col-span-2 flex justify-end">
                <button
                  onClick={() => remove(s.slug || s.id)}
                  disabled={deleting === (s.slug || s.id)}
                  data-testid={`admin-sermon-delete-${s.slug}`}
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-rose-700 hover:text-rose-900 disabled:opacity-50"
                >
                  {deleting === (s.slug || s.id) ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Trash2 size={12} strokeWidth={1.5} />
                  )}
                  Excluir
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
